import React, { useState, useEffect, useCallback } from 'react'; // Added useCallback
import axios from 'axios';
import { DeleteConfirmationModal } from '../common';
import Button from '../common/Button';
import SaveSegmentModal from './SaveSegmentModal'; // Import the SaveSegmentModal
import { canPerformSegmentAction } from '../../utils/permissions';

const SegmentsListModal = ({ isOpen, onClose, onSelectSegment }) => {
  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedSegment, setSelectedSegment] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCreateManualModal, setShowCreateManualModal] = useState(false); // State for manual create modal

  // Fetch segments when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchSegments();
    }
  }, [isOpen]);

  // Function to fetch segments from API
  const fetchSegments = async () => {
    try {
      setLoading(true);
      setError('');

      const token = localStorage.getItem('token');
      // Using X-Auth-Token which is the standard approach in this app
      const response = await axios.get('/api/segments', {
        headers: {
          'x-auth-token': token
        },
        params: {
          include_counts: 'true' // Request lead counts for segments
        }
      });

      // Validate data - if not an array, initialize as empty array
      let segmentsArray = [];
      if (Array.isArray(response.data)) {
        segmentsArray = response.data;
      } else {
        console.error('Unexpected response format - not an array:', response.data);
      }
      
      // Set data
      setSegments(segmentsArray);
    } catch (err) {
      console.error('Error fetching segments:', err);
      setError('Failed to load segments. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Function to handle segment deletion
  const handleDeleteSegment = async () => {
    if (!selectedSegment) return;

    try {
      setLoading(true);

      const token = localStorage.getItem('token');

      await axios.delete(`/api/segments/${selectedSegment.id}`, {
        headers: {
          'x-auth-token': token
        }
      });

      // Remove deleted segment from the list
      setSegments(segments.filter(s => s.id !== selectedSegment.id));
      setShowDeleteModal(false);
      setSelectedSegment(null);
    } catch (err) {
      console.error('Error deleting segment:', err);
      setError('Failed to delete segment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle selecting a segment to view leads
  const handleSelect = (segment) => {
    onSelectSegment(segment);
    onClose();
  };

  // Handle opening the modal to create a manual segment
  const handleOpenCreateManualModal = () => {
      setShowCreateManualModal(true);
  };

  // Handle the actual creation after getting data from SaveSegmentModal
  const handleCreateManualSegment = async (segmentData) => {
      try {
          setLoading(true);
          const token = localStorage.getItem('token');
          await axios.post('/api/segments',
              {
                  name: segmentData.name,
                  description: segmentData.description,
                  is_dynamic: false, // Explicitly set to false for manual
                  is_public: segmentData.is_public, // Respect user choice if allowed
                  filter_config: null // No filters for manual
              },
              { headers: { 'x-auth-token': token } }
          );
          setShowCreateManualModal(false); // Close the modal
          fetchSegments(); // Refresh the list
      } catch (err) {
          console.error('Error creating manual segment:', err);
          setError('Failed to create manual segment.');
          // Keep the modal open on error? Or close and show error in list modal?
          // For now, keep modal open by not calling setShowCreateManualModal(false) on error.
      } finally {
          setLoading(false);
      }
      /* Example API call structure (needs implementation):
      const segmentName = prompt("Enter name for manual segment:");
      if (segmentName) {
          const token = localStorage.getItem('token');
          axios.post('/api/segments',
              { name: segmentName, is_dynamic: false, is_public: false }, // Send is_dynamic: false
              { headers: { 'x-auth-token': token } }
          ).then(() => {
              fetchSegments(); // Refresh list
          }).catch(err => {
              setError('Failed to create manual segment.');
              console.error(err);
          });
      }
      */
  };


  // Handle ESC key press
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEsc);

    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed z-50 inset-0 overflow-y-auto">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true" onClick={onClose}>
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Saved Segments
                </h3>
                <div className="mt-2">
                  <p className="text-sm text-gray-500">
                    Select a saved segment to view its leads
                  </p>

                  {error && (
                    <div className="mt-2 p-2 bg-red-50 border-l-4 border-red-400 text-red-700">
                      {error}
                    </div>
                  )}

                  {loading && segments.length === 0 ? (
                    <div className="flex justify-center items-center p-6">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                    </div>
                  ) : segments.length === 0 ? (
                    <div className="text-center p-6 space-y-4">
                      <p className="text-gray-500">No saved segments found</p>
                      <div className="bg-blue-50 p-4 rounded-md text-left">
                        <h4 className="text-sm font-medium text-blue-800 mb-2">How to create a segment:</h4>
                        <ol className="text-sm text-blue-700 list-decimal pl-5 space-y-2">
                          <li>Go to the leads list</li>
                          <li>Apply one or more filters to find the leads you want</li>
                          <li>Click the <span className="font-medium">Save as Segment</span> button</li>
                          <li>Or, click <span className="font-medium">New Segment (Manual)</span> below to create an empty list to add leads to later.</li>
                        </ol>
                        <p className="text-xs text-blue-600 mt-3 italic">Segments help you quickly access filtered lead lists without having to re-apply filters each time.</p>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-4 overflow-y-auto max-h-96">
                      <ul className="divide-y divide-gray-200">
                        
                        {segments.map((segment, index) => {
                          // Skip rendering if segment is completely null/undefined
                          if (!segment) {
                            console.error('Null segment at index', index);
                            return null;
                          }
                          
                          
                          // Generate a safe key that won't change
                          const key = segment.id || `segment-${index}`;
                          
                          // Use a try-catch block to handle any rendering errors for this segment
                          try {
                            return (
                              <li key={key} className="py-3 flex items-center justify-between hover:bg-gray-50 px-2">
                                <div
                                  className="flex-1 cursor-pointer"
                                  onClick={() => handleSelect(segment)}
                                >
                                  <div className="flex items-center">
                                    <span className="text-sm font-medium text-gray-900">
                                      {segment.name || `Segment #${index + 1}`}
                                      <span className="ml-2 text-xs font-medium text-gray-500">
                                        ({segment.lead_count || 0} leads
                                        {segment.subscribed_count !== undefined && (
                                          <span>, <span className="text-green-600">{segment.subscribed_count}</span> subscribed</span>
                                        )})
                                      </span>
                                    </span>
                                    {segment.is_public ? (
                                      <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                                        </svg>
                                        Available to all users
                                      </span>
                                    ) : (
                                      <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                        </svg>
                                        Only you
                                      </span>
                                    )}
                                  </div>
                                  {segment.description && (
                                    <p className="text-xs text-gray-500 mt-1">{segment.description}</p>
                                  )}
                                  <div className="text-xs text-gray-500 mt-1">
                                    {segment.is_dynamic !== false ? (
                                      <span className="flex items-center">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                        </svg>
                                        Updates automatically (Filter-based)
                                      </span>
                                    ) : (
                                      <span className="flex items-center">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                                        </svg>
                                        Manual list
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <div className="ml-4 flex-shrink-0">
                                  <Button
                                    variant="link"
                                    onClick={() => {
                                      setSelectedSegment(segment);
                                      setShowDeleteModal(true);
                                    }}
                                    className="text-red-600 hover:text-red-900 text-sm font-medium"
                                  >
                                    Delete
                                  </Button>
                                </div>
                              </li>
                            );
                          } catch (err) {
                            console.error(`Error rendering segment ${index}:`, segment, err);
                            return (
                              <li key={key || 'error-segment'} className="py-3 flex px-2">
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-gray-900">
                                    {segment.name || `Segment #${index + 1}`}
                                  </p>
                                  <p className="text-xs text-red-600">
                                    Error rendering segment details. See console for details.
                                  </p>
                                </div>
                                <div className="ml-4 flex-shrink-0">
                                  <Button
                                    variant="link"
                                    onClick={() => {
                                      setSelectedSegment(segment);
                                      setShowDeleteModal(true);
                                    }}
                                    className="text-red-600 hover:text-red-900 text-sm font-medium"
                                  >
                                    Delete
                                  </Button>
                                </div>
                              </li>
                            );
                          }
                        })}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
             {/* Add New Manual Segment Button */}
             {(canPerformSegmentAction('create_own') || canPerformSegmentAction('create_delete_own') || canPerformSegmentAction('create_public')) && (
                 <Button
                   variant="primary"
                   onClick={handleOpenCreateManualModal} // Updated handler
                   // Removed mr-3 from here
                 >
                   New Segment (Manual)
                 </Button>
             )}
            <Button
              variant="outline"
              onClick={onClose}
              className="mr-3" // Changed ml-3 to mr-3 for flex-row-reverse
            >
              Close
            </Button>
          </div>
        </div>
      </div>

      {/* Delete confirmation modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedSegment(null);
        }}
        onConfirm={handleDeleteSegment}
        title="Delete Segment"
        message={`Are you sure you want to delete the segment "${selectedSegment?.name || ''}"? This action cannot be undone.`}
      />

      {/* Modal for Creating Manual Segment (reuses SaveSegmentModal) */}
      <SaveSegmentModal
        isOpen={showCreateManualModal}
        onClose={() => setShowCreateManualModal(false)}
        onSave={handleCreateManualSegment} // Pass the creation handler
        manualMode={true} // Indicate manual mode
        // Pass empty filters as manual segments don't have them
        filters={{}}
        activeFilters={{}}
        users={[]} // Users might not be needed for manual creation, pass empty or fetch if needed for ownership/public toggle
      />
    </div>
  );
};

export default SegmentsListModal;
